import { useState, useContext } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ScrollText, Shield, Cookie, FileText, ArrowLeft, Download } from "lucide-react";
import { AuthContext } from "@/App";
import { legalTexts } from "@/data/legalTexts";
import ReactMarkdown from "react-markdown";

const SECTIONS = [
  { key: "terms",   icon: ScrollText, dataKey: "termsAndConditions",
    label: { ca: "Termes i Condicions",     es: "Términos y Condiciones" } },
  { key: "privacy", icon: Shield,     dataKey: "privacyPolicy",
    label: { ca: "Política de Privacitat",  es: "Política de Privacidad" } },
  { key: "cookies", icon: Cookie,     dataKey: "cookiesPolicy",
    label: { ca: "Política de Cookies",     es: "Política de Cookies" } },
  { key: "legal",   icon: FileText,   dataKey: "legalNotice",
    label: { ca: "Avís Legal",              es: "Aviso Legal" } },
];

const LegalPage = () => {
  const [searchParams] = useSearchParams();
  const initialSection = searchParams.get("section") || "terms";
  const [activeSection, setActiveSection] = useState(initialSection);
  const authContext = useContext(AuthContext);
  const userLang = authContext?.language || "es";
  const [language, setLanguage] = useState(userLang);

  const lang = language === "ca" ? "ca" : "es";
  const currentSection = SECTIONS.find(s => s.key === activeSection) || SECTIONS[0];
  const data = legalTexts[currentSection.dataKey][lang];
  const ActiveIcon = currentSection.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back button */}
        <Link
          to={authContext?.user ? "/dashboard" : "/auth"}
          data-testid="legal-back-btn"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-teal-300 transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          {lang === "ca" ? "Tornar" : "Volver"}
        </Link>

        {/* Tab Switcher - 4 secciones en grid 2x2 */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          {SECTIONS.map(section => {
            const Icon = section.icon;
            const isActive = activeSection === section.key;
            return (
              <button
                key={section.key}
                data-testid={`legal-tab-${section.key}`}
                onClick={() => setActiveSection(section.key)}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                  isActive
                    ? "bg-teal-500/20 text-teal-300 border border-teal-500/30"
                    : "bg-slate-800/50 text-gray-400 border border-slate-700/30 hover:text-gray-300"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{section.label[lang]}</span>
              </button>
            );
          })}
        </div>

        {/* Language Switcher + Download */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex gap-1">
            <button
              data-testid="legal-page-lang-ca"
              onClick={() => setLanguage("ca")}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                lang === "ca" ? "bg-teal-500/20 text-teal-300 border border-teal-500/30" : "text-gray-400 hover:text-gray-300 border border-transparent"
              }`}
            >
              Català
            </button>
            <button
              data-testid="legal-page-lang-es"
              onClick={() => setLanguage("es")}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                lang === "es" ? "bg-teal-500/20 text-teal-300 border border-teal-500/30" : "text-gray-400 hover:text-gray-300 border border-transparent"
              }`}
            >
              Español
            </button>
          </div>

          {data.pdf && (
            <a
              data-testid={`legal-download-${activeSection}`}
              href={data.pdf}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg bg-slate-800/50 text-gray-300 hover:bg-slate-700/70 hover:text-white border border-slate-700/40 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              {lang === "ca" ? "Descarregar PDF" : "Descargar PDF"}
            </a>
          )}
        </div>

        {/* Content Card */}
        <div className="glass rounded-2xl p-6 md:p-8 border border-teal-400/10">
          <div className="flex items-center gap-3 mb-2">
            <ActiveIcon className="w-6 h-6 text-teal-400" />
            <h1 className="text-xl md:text-2xl font-bold text-white">{data.title}</h1>
          </div>
          <p className="text-xs text-gray-500 mb-6">{data.lastUpdated}</p>

          <div className="prose prose-invert prose-sm max-w-none
            prose-headings:text-teal-300 prose-headings:font-semibold
            prose-h2:text-base prose-h2:mt-6 prose-h2:mb-3
            prose-h3:text-sm prose-h3:mt-4 prose-h3:mb-2
            prose-p:text-gray-300 prose-p:leading-relaxed prose-p:text-sm
            prose-li:text-gray-300 prose-li:text-sm
            prose-strong:text-white
            prose-a:text-teal-300 prose-a:no-underline hover:prose-a:underline
            prose-table:text-xs prose-th:text-teal-300 prose-td:text-gray-300
            prose-ul:my-2 prose-ol:my-2
          ">
            <ReactMarkdown>{data.content}</ReactMarkdown>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-gray-600">
          ESSENC·IA — hola@essencia-app.com
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
